import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getWoCategories, getDepartments } from '../api/client'

export interface WoCategoryCfg {
  id: number; code: string; name: string; nameTh?: string | null
  departmentCode?: string | null; appliesTo?: string | null; isActive?: boolean
}
export interface DeptCfg {
  id: number; code: string; name: string; nameEn?: string | null; nameTh?: string | null; isActive?: boolean
}

// ประเภทงานตั้งต้นของระบบ — ใช้เป็น fallback ตอนยังโหลดรายการจาก Configuration ไม่ได้
const BUILTIN_CATEGORIES = ['ELECTRICAL', 'PLUMBING', 'IT', 'HVAC', 'OTHER']

/**
 * โหลดแผนก + ประเภทงานจาก Configuration แล้วให้ตัวช่วยแสดงชื่อตามภาษาที่เลือก (ไทย/อังกฤษ)
 * และสร้างตัวเลือกสำหรับ Select ที่ค้นหาได้ — ใช้ร่วมกันทั้งหน้า Work Order, Dashboard และ Company Profile
 * เพื่อให้ชื่อแผนก/ประเภทงานแสดงตรงกันทุกที่
 */
export function useWorkOrderMeta() {
  const { t, i18n } = useTranslation()
  const th = i18n.language?.startsWith('th')
  const [categories, setCategories] = useState<WoCategoryCfg[]>([])
  const [departments, setDepartments] = useState<DeptCfg[]>([])

  useEffect(() => {
    getWoCategories().then(r => setCategories(r.data ?? [])).catch(() => {})
    getDepartments().then(r => setDepartments(r.data ?? [])).catch(() => {})
  }, [])

  const catByCode = useMemo(() => new Map(categories.map(c => [c.code, c])), [categories])
  const deptByCode = useMemo(() => new Map(departments.map(d => [d.code, d])), [departments])

  /** ชื่อประเภทงานตามภาษา: ไทย → nameTh | คำแปลระบบ | name ; อังกฤษ → name (ถ้ามี nameTh คู่กัน) | คำแปลระบบ | name */
  const catLabel = useCallback((code?: string | null): string => {
    if (!code) return '—'
    const cfg = catByCode.get(code)
    const builtIn = i18n.exists(`workOrders.category.${code}`) ? t(`workOrders.category.${code}`) : null
    if (th) return cfg?.nameTh || builtIn || cfg?.name || code
    return (cfg?.nameTh ? cfg.name : builtIn || cfg?.name) || code
  }, [catByCode, th, t, i18n])

  /** ชื่อแผนกตามภาษา — ถ้าไม่มีในรายการ (ถูกลบ/ปิดไปแล้ว) แสดงรหัสเดิมแทน */
  const deptLabel = useCallback((code?: string | null): string => {
    if (!code) return '—'
    const d = deptByCode.get(code)
    if (!d) return code
    return (th ? d.nameTh || d.name : d.nameEn || d.name) || code
  }, [deptByCode, th])

  const activeCategories = useMemo(
    () => categories.filter(c => c.isActive !== false && c.appliesTo !== 'HKO'),
    [categories],
  )

  /** ตัวเลือกประเภทงาน — ใส่ deptCode เพื่อเอาเฉพาะประเภทของแผนกนั้น + ประเภทที่ไม่ผูกแผนก */
  const categoryOptions = useCallback((deptCode?: string | null, includeCode?: string | null) => {
    const list = activeCategories.length
      ? activeCategories.filter(c => !deptCode || !c.departmentCode || c.departmentCode === deptCode || c.code === includeCode)
      : BUILTIN_CATEGORIES.map(code => ({ code } as WoCategoryCfg))
    return list.map(c => ({ value: c.code, label: catLabel(c.code) }))
  }, [activeCategories, catLabel])

  const departmentOptions = useMemo(
    () => departments.filter(d => d.isActive !== false).map(d => ({ value: d.code, label: `${deptLabel(d.code)} (${d.code})` })),
    [departments, deptLabel],
  )

  /** แผนกที่ผูกกับประเภทงานนี้ใน Configuration (ถ้ามี) */
  const deptOfCategory = useCallback((code?: string | null) => (code ? catByCode.get(code)?.departmentCode ?? null : null), [catByCode])

  /** วันที่-เวลาตามภาษา เขตเวลาไทย (เช่น 10 ต.ค. 2569 14:35 / 10 Oct 2026, 14:35) */
  const fmtDateTime = useCallback((iso?: string | null): string => {
    if (!iso) return '—'
    return new Date(iso).toLocaleString(th ? 'th-TH' : 'en-GB', {
      timeZone: 'Asia/Bangkok', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false,
    })
  }, [th])

  return { categories, departments, catLabel, deptLabel, categoryOptions, departmentOptions, deptOfCategory, fmtDateTime }
}
